// ✅ Trekkin.app — navGridPersistent.js
// -----------------------------------------------------------------------------
// Persistência robusta da navGrid:
// - Web: IndexedDB
// - Native (iOS/Android via Capacitor): Filesystem (Directory.DATA)
//
// Objetivo: evitar falhas silenciosas do localStorage (quota/tamanho) e manter a
// grelha disponível entre reinstalações/updates sem bloquear o arranque.
// -----------------------------------------------------------------------------

import { loadNavGrid, normalizeNavGrid, saveNavGrid } from "./navGrid.js";
import { logger } from "./logger.js";

const IDB_DB_NAME = "trekkin-navgrid";
const IDB_STORE = "kv";
const IDB_VERSION = 1;
const IDB_KEY = "navGrid_v1";
const NAVGRID_DEBUG_KEY = "trekkin.navgrid.debug";

const NATIVE_FILENAME = "navGrid_v1.json";
const NATIVE_CHUNK_MANIFEST = "navGrid_v2_manifest.json";
const NATIVE_CHUNK_PREFIX = "navGrid_v2";
const NATIVE_CHUNK_SIZE = 512 * 1024;
const NATIVE_DIR = "DATA";
const ANDROID_RESUME_PERSIST_DEFER_MS = 15000;

const hasIOSNativeBridge = () => {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage === "function"
    );
  } catch {
    return false;
  }
};

function iosNavGridRequest(action, payload = {}) {
  return new Promise((resolve, reject) => {
    if (!hasIOSNativeBridge()) {
      reject(new Error("ios_native_bridge_unavailable"));
      return;
    }
    const requestId = `navgrid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const timeout = window.setTimeout(() => {
      window.removeEventListener("native:navGridFile", handler);
      reject(new Error("ios_navgrid_timeout"));
    }, 30000);
    const handler = (event) => {
      if (event?.detail?.requestId !== requestId) return;
      window.clearTimeout(timeout);
      window.removeEventListener("native:navGridFile", handler);
      if (event.detail.status === "ok") resolve(event.detail);
      else reject(new Error(event.detail.message || "ios_navgrid_failed"));
    };
    window.addEventListener("native:navGridFile", handler);
    window.webkit.messageHandlers.nativeBridge.postMessage({ action, requestId, ...payload });
  });
}

async function readIOSNativeUtf8(path) {
  const result = await iosNavGridRequest("readNavGridFile", { path });
  return typeof result?.data === "string" ? result.data : null;
}

async function deleteIOSNativeFile(path) {
  try {
    await iosNavGridRequest("deleteNavGridFile", { path });
    return true;
  } catch {
    return false;
  }
}

function isDebugEnabled() {
  if (typeof window === "undefined") return true;
  try {
    const raw = String(window.localStorage?.getItem?.(NAVGRID_DEBUG_KEY) ?? "").trim().toLowerCase();
    if (!raw) return true;
    return raw === "1" || raw === "true" || raw === "on";
  } catch {
    return true;
  }
}

function navGridLog(level, message, payload = null) {
  const lvl = String(level || "debug").toLowerCase();
  if (lvl === "debug" && !isDebugEnabled()) return;
  const msg = String(message || "navgrid");
  if (lvl === "error") logger.error("NAVGRID_PERSIST", msg, payload || null);
  else if (lvl === "warn") logger.warn("NAVGRID_PERSIST", msg, payload || null);
  else if (lvl === "info") logger.info("NAVGRID_PERSIST", msg, payload || null);
  else logger.debug("NAVGRID_PERSIST", msg, payload || null);
}

const isNativeFsAvailable = () =>
  typeof window !== "undefined" &&
  !!window.Capacitor?.isNativePlatform?.() &&
  !!window.Capacitor?.Plugins?.Filesystem?.readFile &&
  !!window.Capacitor?.Plugins?.Filesystem?.writeFile;

function getNativePlatform() {
  try {
    if (typeof window === "undefined") return "";
    if (!window.Capacitor?.isNativePlatform?.()) return "";
    return String(window.Capacitor?.getPlatform?.() || "").toLowerCase();
  } catch {
    return "";
  }
}

function resolvePersistDelayMs(requestedDelayMs) {
  const baseDelayMs = Math.max(0, Number(requestedDelayMs) || 0);
  if (getNativePlatform() !== "android") return baseDelayMs;
  try {
    const resumeStartedAt = Number(window?.__TREKKIN_ANDROID_RESUME_PERF?.startedAt || 0);
    const foregroundAt = Number(window?.__TREKKIN_ANDROID_FOREGROUND_AT || 0);
    const startedAt = Math.max(resumeStartedAt, foregroundAt);
    if (!startedAt) return baseDelayMs;
    const elapsedMs = Date.now() - startedAt;
    if (elapsedMs < 0 || elapsedMs >= ANDROID_RESUME_PERSIST_DEFER_MS) return baseDelayMs;
    return Math.max(baseDelayMs, ANDROID_RESUME_PERSIST_DEFER_MS - elapsedMs);
  } catch {
    return baseDelayMs;
  }
}

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGetString(key) {
  const db = await openDB().catch(() => null);
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => {
        const row = req.result;
        db.close();
        resolve(typeof row?.data === "string" ? row.data : null);
      };
      req.onerror = () => {
        db.close();
        resolve(null);
      };
      tx.onerror = () => {
        db.close();
        resolve(null);
      };
    } catch {
      try {
        db.close();
      } catch {}
      resolve(null);
    }
  });
}

async function idbPutString(key, data) {
  const db = await openDB().catch(() => null);
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put({ key, data });
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        resolve(false);
      };
    } catch {
      try {
        db.close();
      } catch {}
      resolve(false);
    }
  });
}

async function idbDelete(key) {
  const db = await openDB().catch(() => null);
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        resolve(false);
      };
    } catch {
      try {
        db.close();
      } catch {}
      resolve(false);
    }
  });
}

function fromBase64(b64) {
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return null;
  }
}

function nativeChunkFilename(generation, index) {
  return `${NATIVE_CHUNK_PREFIX}_${generation}_${String(index).padStart(4, "0")}.json`;
}

async function readNativeUtf8(fs, path) {
  const res = await fs.readFile({
    path,
    directory: NATIVE_DIR,
    encoding: "utf8",
  });
  return typeof res?.data === "string" ? res.data : null;
}

async function readNativeChunkManifest(fs) {
  try {
    const raw = await readNativeUtf8(fs, NATIVE_CHUNK_MANIFEST);
    const manifest = raw ? JSON.parse(raw) : null;
    const generation = String(manifest?.generation || "");
    const parts = Number(manifest?.parts);
    const chars = Number(manifest?.chars);
    if (!generation || !Number.isSafeInteger(parts) || parts < 1 || parts > 10000) return null;
    if (!Number.isSafeInteger(chars) || chars < 1) return null;
    return { generation, parts, chars };
  } catch {
    return null;
  }
}

async function deleteNativeFile(fs, path) {
  if (!fs?.deleteFile) return false;
  try {
    await fs.deleteFile({ path, directory: NATIVE_DIR });
    return true;
  } catch {
    return false;
  }
}

async function deleteNativeChunkGeneration(fs, manifest) {
  if (!manifest?.generation || !Number.isSafeInteger(manifest?.parts)) return;
  for (let index = 0; index < manifest.parts; index += 1) {
    await deleteNativeFile(fs, nativeChunkFilename(manifest.generation, index));
  }
}

function nextNativeChunk(json, offset) {
  let end = Math.min(json.length, offset + NATIVE_CHUNK_SIZE);
  const lastCode = json.charCodeAt(end - 1);
  const nextCode = json.charCodeAt(end);
  const splitsSurrogatePair =
    lastCode >= 0xd800 &&
    lastCode <= 0xdbff &&
    nextCode >= 0xdc00 &&
    nextCode <= 0xdfff;
  if (splitsSurrogatePair) end -= 1;
  return { data: json.slice(offset, end), end };
}

async function loadNativeChunkedGrid(fs) {
  const manifest = await readNativeChunkManifest(fs);
  if (!manifest) return null;

  const chunks = [];
  let chars = 0;
  for (let index = 0; index < manifest.parts; index += 1) {
    const chunk = await readNativeUtf8(fs, nativeChunkFilename(manifest.generation, index));
    if (typeof chunk !== "string") return null;
    chunks.push(chunk);
    chars += chunk.length;
  }
  if (chars !== manifest.chars) return null;
  return JSON.parse(chunks.join(""));
}

export async function loadNavGridPersistentRaw() {
  if (hasIOSNativeBridge()) {
    try {
      const rawManifest = await readIOSNativeUtf8(NATIVE_CHUNK_MANIFEST);
      const manifest = rawManifest ? JSON.parse(rawManifest) : null;
      const generation = String(manifest?.generation || "");
      const parts = Number(manifest?.parts);
      const chars = Number(manifest?.chars);
      if (!generation || !Number.isSafeInteger(parts) || parts < 1 || parts > 10000) return null;
      const chunks = [];
      let actualChars = 0;
      for (let index = 0; index < parts; index += 1) {
        const chunk = await readIOSNativeUtf8(nativeChunkFilename(generation, index));
        if (typeof chunk !== "string") return null;
        chunks.push(chunk);
        actualChars += chunk.length;
      }
      if (actualChars !== chars) return null;
      return JSON.parse(chunks.join(""));
    } catch {
      return null;
    }
  }

  // Native: Filesystem
  if (isNativeFsAvailable()) {
    const fs = window.Capacitor.Plugins.Filesystem;
    try {
      const chunked = await loadNativeChunkedGrid(fs);
      if (chunked) return chunked;
    } catch {}
    try {
      const res = await fs.readFile({
        path: NATIVE_FILENAME,
        directory: NATIVE_DIR,
        encoding: "utf8",
      });
      if (typeof res?.data === "string" && res.data.trim()) {
        return JSON.parse(res.data);
      }
    } catch {}
    try {
      const res = await fs.readFile({ path: NATIVE_FILENAME, directory: NATIVE_DIR });
      if (typeof res?.data === "string" && res.data.trim()) {
        const decoded = fromBase64(res.data);
        if (decoded) return JSON.parse(decoded);
      }
    } catch {}
    return null;
  }

  // Web: IndexedDB
  const raw = await idbGetString(IDB_KEY);
  if (!raw || !raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export async function saveNavGridPersistentRaw(grid) {
  const json = (() => {
    try {
      return JSON.stringify(grid);
    } catch {
      return null;
    }
  })();
  if (!json) return false;

  if (hasIOSNativeBridge()) {
    let previousManifest = null;
    try {
      const raw = await readIOSNativeUtf8(NATIVE_CHUNK_MANIFEST);
      previousManifest = raw ? JSON.parse(raw) : null;
    } catch {}
    const generation = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    let writtenParts = 0;
    try {
      let offset = 0;
      while (offset < json.length) {
        const chunk = nextNativeChunk(json, offset);
        await iosNavGridRequest("writeNavGridFile", {
          path: nativeChunkFilename(generation, writtenParts),
          data: chunk.data,
        });
        writtenParts += 1;
        offset = chunk.end;
      }
      await iosNavGridRequest("writeNavGridFile", {
        path: NATIVE_CHUNK_MANIFEST,
        data: JSON.stringify({ version: 2, generation, parts: writtenParts, chars: json.length,
          updatedAt: Number(grid?.updatedAt || 0) || 0 }),
      });
      if (previousManifest?.generation && previousManifest.generation !== generation) {
        for (let index = 0; index < Number(previousManifest.parts || 0); index += 1) {
          await deleteIOSNativeFile(nativeChunkFilename(previousManifest.generation, index));
        }
      }
      return true;
    } catch (err) {
      for (let index = 0; index < writtenParts; index += 1) {
        await deleteIOSNativeFile(nativeChunkFilename(generation, index));
      }
      navGridLog("error", "save persistent failed (ios native chunked)", {
        error: String(err?.message || err || "unknown"), writtenParts,
      });
      return false;
    }
  }

  // Native: Filesystem
  if (isNativeFsAvailable()) {
    const fs = window.Capacitor.Plugins.Filesystem;
    const previousManifest = await readNativeChunkManifest(fs);
    const generation = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const estimatedParts = Math.ceil(json.length / NATIVE_CHUNK_SIZE);
    let writtenParts = 0;
    navGridLog("debug", "save persistent start (native fs)", {
      segments: Array.isArray(grid?.segments) ? grid.segments.length : 0,
      updatedAt: Number(grid?.updatedAt || 0) || 0,
      bytes: json.length,
      estimatedParts,
    });
    try {
      let offset = 0;
      while (offset < json.length) {
        const chunk = nextNativeChunk(json, offset);
        await fs.writeFile({
          path: nativeChunkFilename(generation, writtenParts),
          directory: NATIVE_DIR,
          encoding: "utf8",
          data: chunk.data,
        });
        writtenParts += 1;
        offset = chunk.end;
      }
      await fs.writeFile({
        path: NATIVE_CHUNK_MANIFEST,
        directory: NATIVE_DIR,
        encoding: "utf8",
        data: JSON.stringify({
          version: 2,
          generation,
          parts: writtenParts,
          chars: json.length,
          updatedAt: Number(grid?.updatedAt || 0) || 0,
        }),
      });
      try {
        window.localStorage?.removeItem?.("trekkin_nav_grid_v1");
      } catch {}
      await deleteNativeFile(fs, NATIVE_FILENAME);
      if (previousManifest?.generation !== generation) {
        await deleteNativeChunkGeneration(fs, previousManifest);
      }
      navGridLog("info", "save persistent ok (native fs chunked)", { parts: writtenParts });
      return true;
    } catch (err) {
      await deleteNativeChunkGeneration(fs, { generation, parts: writtenParts });
      navGridLog("error", "save persistent failed (native fs chunked)", {
        error: String(err?.message || err || "unknown"),
        writtenParts,
      });
      return false;
    }
  }

  // Web: IndexedDB
  navGridLog("debug", "save persistent start (indexeddb)", {
    segments: Array.isArray(grid?.segments) ? grid.segments.length : 0,
    updatedAt: Number(grid?.updatedAt || 0) || 0,
    bytes: json.length,
  });
  const ok = await idbPutString(IDB_KEY, json);
  navGridLog(ok ? "info" : "error", "save persistent end (indexeddb)", { ok });
  return ok;
}

export async function clearNavGridPersistent() {
  if (hasIOSNativeBridge()) {
    let manifest = null;
    try {
      const raw = await readIOSNativeUtf8(NATIVE_CHUNK_MANIFEST);
      manifest = raw ? JSON.parse(raw) : null;
    } catch {}
    for (let index = 0; index < Number(manifest?.parts || 0); index += 1) {
      await deleteIOSNativeFile(nativeChunkFilename(manifest.generation, index));
    }
    return deleteIOSNativeFile(NATIVE_CHUNK_MANIFEST);
  }

  if (isNativeFsAvailable()) {
    const fs = window.Capacitor.Plugins.Filesystem;
    if (!fs?.deleteFile) return false;
    const manifest = await readNativeChunkManifest(fs);
    await deleteNativeChunkGeneration(fs, manifest);
    const deletedManifest = await deleteNativeFile(fs, NATIVE_CHUNK_MANIFEST);
    const deletedLegacy = await deleteNativeFile(fs, NATIVE_FILENAME);
    return deletedManifest || deletedLegacy || !!manifest;
  }
  return idbDelete(IDB_KEY);
}

let hydratePromise = null;
let queuedTimer = null;
let queuedPromise = null;
let queuedLatest = null;
let queuedResolve = null;
let writeInFlight = false;
let lastPersistedSignature = "";

function getGridPersistSignature(grid) {
  if (!grid || typeof grid !== "object") return "";
  const segCount = Array.isArray(grid?.segments) ? grid.segments.length : 0;
  const stamp = Number(grid?.updatedAt || 0) || 0;
  return `${stamp}:${segCount}`;
}

export async function hydrateNavGridFromPersistent(options = {}) {
  const force = options?.force === true;
  // A grelha nativa tem dezenas de MB divididos por muitos ficheiros. Se já
  // está hidratada em memória, voltar a ler e concatenar todos os blocos cria
  // temporariamente várias cópias do mesmo JSON e pode terminar o WebContent
  // no iOS por pressão de memória (nomeadamente ao voltar ao planeador).
  if (!force) {
    const memoryGrid = loadNavGrid();
    if (Array.isArray(memoryGrid?.segments) && memoryGrid.segments.length > 0) {
      return false;
    }
  }
  if (hydratePromise && !force) return hydratePromise;

  hydratePromise = (async () => {
    const raw = await loadNavGridPersistentRaw();
    if (!raw || typeof raw !== "object") return false;

    const incoming = normalizeNavGrid(raw);
    const incomingSegs = Array.isArray(incoming.segments) ? incoming.segments : [];
    if (!incomingSegs.length) return false;
    const incomingSignature = getGridPersistSignature(incoming);
    if (incomingSignature) {
      lastPersistedSignature = incomingSignature;
    }

    const local = loadNavGrid();
    const localSegs = Array.isArray(local.segments) ? local.segments : [];

    const incomingStamp = Number(incoming.updatedAt || 0) || 0;
    const localStamp = Number(local.updatedAt || 0) || 0;

    const shouldApply =
      (!localSegs.length && incomingSegs.length > 0) || incomingStamp > localStamp;

    if (!shouldApply) {
      if (localSegs.length && localStamp > incomingStamp) {
        await saveNavGridPersistentRaw(local);
        const localSignature = getGridPersistSignature(local);
        if (localSignature) {
          lastPersistedSignature = localSignature;
        }
      } else {
        try {
          window.localStorage?.removeItem?.("trekkin_nav_grid_v1");
        } catch {}
      }
      return false;
    }

    // Atualiza cache rápido (mem/localStorage) e notifica UI/mapa.
    const withStamp =
      incomingStamp > 0 ? incoming : { ...incoming, updatedAt: Date.now() };
    saveNavGrid(withStamp);
    window?.dispatchEvent?.(
      new CustomEvent("trekkin:navgrid-updated", { detail: { context: "persistent" } })
    );
    return true;
  })().finally(() => {
    hydratePromise = null;
  });

  return hydratePromise;
}

export async function saveNavGridPersistent(grid) {
  if (!grid || typeof grid !== "object") return false;
  const normalized = normalizeNavGrid(grid);
  return saveNavGridPersistentRaw(normalized);
}

// Guarda em background (coalescing). Útil porque a navGrid pode ser atualizada várias vezes por segundo
// durante edições; não queremos escrever no disco/IndexedDB a cada tick.
export function queueSaveNavGridPersistent(grid, options = {}) {
  const requestedDelayMs = Math.max(0, Number(options?.delayMs ?? 40) || 0);
  const delayMs = resolvePersistDelayMs(requestedDelayMs);
  const segCount = Array.isArray(grid?.segments) ? grid.segments.length : 0;
  const stamp = Number(grid?.updatedAt || 0) || 0;
  const signature = getGridPersistSignature(grid);
  if (!options?.force && signature && signature === lastPersistedSignature) {
    navGridLog("debug", "queue save skipped unchanged", {
      segments: segCount,
      updatedAt: stamp,
    });
    return Promise.resolve(true);
  }
  navGridLog("debug", "queue save", {
    delayMs,
    requestedDelayMs,
    segments: segCount,
    updatedAt: stamp,
  });
  queuedLatest = grid;

  if (queuedTimer) {
    clearTimeout(queuedTimer);
    queuedTimer = null;
  }

  if (!queuedPromise) {
    queuedPromise = new Promise((resolve) => {
      queuedResolve = resolve;
    });
  }

  queuedTimer = setTimeout(async () => {
    queuedTimer = null;
    if (writeInFlight) return;

    const toSave = queuedLatest;
    queuedLatest = null;
    if (!toSave) return;
    const saveSignature = getGridPersistSignature(toSave);
    if (!options?.force && saveSignature && saveSignature === lastPersistedSignature) {
      navGridLog("debug", "flush save skipped unchanged", {
        segments: Array.isArray(toSave?.segments) ? toSave.segments.length : 0,
        updatedAt: Number(toSave?.updatedAt || 0) || 0,
      });
      const resolve = queuedResolve;
      queuedPromise = null;
      queuedResolve = null;
      if (typeof resolve === "function") resolve(true);
      return;
    }

    writeInFlight = true;
    const t0 = Date.now();
    navGridLog("debug", "flush save start", {
      segments: Array.isArray(toSave?.segments) ? toSave.segments.length : 0,
      updatedAt: Number(toSave?.updatedAt || 0) || 0,
    });
    let ok = false;
    try {
      ok = await saveNavGridPersistent(toSave);
    } catch (err) {
      navGridLog("error", "flush save failed", {
        error: String(err?.message || err || "unknown"),
      });
    } finally {
      writeInFlight = false;
    }
    navGridLog(ok ? "info" : "error", "flush save end", {
      ok,
      elapsedMs: Date.now() - t0,
    });
    if (ok && saveSignature) {
      lastPersistedSignature = saveSignature;
    }

    if (queuedLatest) {
      queueSaveNavGridPersistent(queuedLatest, { delayMs: 0 });
      return;
    }

    const resolve = queuedResolve;
    queuedPromise = null;
    queuedResolve = null;
    if (typeof resolve === "function") resolve(ok);
  }, delayMs);

  return queuedPromise;
}
